import { plateFont, numFont } from '../../utils/fonts';
import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  IconButton,
  Typography,
  Grid,
  Divider,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Collapse,
} from '@mui/material';
import { withTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TireRepairIcon from '@mui/icons-material/TireRepair';
import StraightenIcon from '@mui/icons-material/Straighten';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import axios from 'axios';
import { useSubscriptionStore } from '../../store';
import { InternalNoteEditor } from '../shared/InternalNote';
import { VehicleServiceInfoEditor } from '../shared/VehicleServiceInfo';
import usePermissions from '../../hooks/usePermissions';

const VEHICLE_CATEGORY_MAP = {
  M1: 'vehicle.categoryM1', M1G: 'vehicle.categoryM1G',
  M2: 'vehicle.categoryM2', M3: 'vehicle.categoryM3',
  N1: 'vehicle.categoryN1', N1G: 'vehicle.categoryN1G',
  N2: 'vehicle.categoryN2', N3: 'vehicle.categoryN3',
  L1e: 'vehicle.categoryL1e', L3e: 'vehicle.categoryL3e',
  L5e: 'vehicle.categoryL5e', L6e: 'vehicle.categoryL6e',
  L7e: 'vehicle.categoryL7e', T: 'vehicle.categoryT', C: 'vehicle.categoryC',
};

function InfoRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
      <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
}

function InfoSection({ icon, title, items }) {
  // Filter items that have values
  const validItems = items.filter(item => item.value || item.value === 0);
  if (validItems.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon}
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'text.primary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ bgcolor: 'background.default', borderRadius: '10px', px: 2, py: 0.5 }}>
        {validItems.map((item, idx) => (
          <React.Fragment key={idx}>
            <InfoRow label={item.label} value={item.value} />
            {idx < validItems.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

function VehicleInfoModal({ t, open, handleClose, vehicle, onVehicleUpdated }) {
  const [lookupError, setLookupError] = useState(null);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editData, setEditData] = useState({});

  // Subscription/trial check
  const isTrial = useSubscriptionStore((state) => state.isTrial);
  const { can } = usePermissions();

  // Initialize edit data when vehicle changes
  useEffect(() => {
    if (vehicle) {
      setEditData({
        registration_number: vehicle.registration_number || '',
        manufacturer: vehicle.manufacturer || '',
        model: vehicle.model || '',
        model_variant: vehicle.model_variant || '',
        year_model: vehicle.year_model || '',
        vin: vehicle.vin || '',
        fuel: vehicle.fuel || '',
        engine_code: vehicle.engine_code || '',
        displacement_cc: vehicle.displacement_cc || '',
        power_kw: vehicle.power_kw || '',
      });
    }
  }, [vehicle]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setIsEditing(false);
      setLookupError(null);
      setLookupSuccess(false);
    }
  }, [open]);

  if (!open) return null;

  // Check if vehicle data is incomplete (missing key technical info)
  const isVehicleDataIncomplete = !vehicle.vin || !vehicle.engine_code || !vehicle.fuel;

  const handleEditChange = (field) => (e) => {
    setEditData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSaveVehicle = async () => {
    if (!vehicle.id) return;

    setSaveLoading(true);
    try {
      const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, editData);
      if (onVehicleUpdated) {
        onVehicleUpdated(response.data);
      }
      setIsEditing(false);
      setLookupSuccess(true);
    } catch (error) {
      console.error('Vehicle save failed:', error);
      setLookupError(t('Failed to save vehicle info'));
    } finally {
      setSaveLoading(false);
    }
  };

  // Only the note is sent, so the vehicle stays eligible for the automatic registry lookup.
  const handleSaveInternalNote = async (text) => {
    const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, { internal_note: text });
    if (onVehicleUpdated) {
      onVehicleUpdated(response.data);
    }
  };

  // Same for the service info: sent alone, it does not mark the vehicle data manually edited.
  const handleSaveServiceInfo = async (text) => {
    const response = await axios.patch(`/api/vehicles/${vehicle.id}/`, { service_info: text });
    if (onVehicleUpdated) {
      onVehicleUpdated(response.data);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset edit data to current vehicle values
    setEditData({
      registration_number: vehicle.registration_number || '',
      manufacturer: vehicle.manufacturer || '',
      model: vehicle.model || '',
      model_variant: vehicle.model_variant || '',
      year_model: vehicle.year_model || '',
      vin: vehicle.vin || '',
      fuel: vehicle.fuel || '',
      engine_code: vehicle.engine_code || '',
      displacement_cc: vehicle.displacement_cc || '',
      power_kw: vehicle.power_kw || '',
    });
  };

  const formatPower = () => {
    if (vehicle.power_kw && vehicle.power_hp) {
      return `${vehicle.power_kw} kW / ${vehicle.power_hp} hp`;
    }
    if (vehicle.power_kw) return `${vehicle.power_kw} kW`;
    if (vehicle.power_hp) return `${vehicle.power_hp} hp`;
    return null;
  };

  const formatDisplacement = () => {
    if (vehicle.displacement_liters && vehicle.displacement_cc) {
      return `${vehicle.displacement_liters} L (${vehicle.displacement_cc} cc)`;
    }
    if (vehicle.displacement_liters) return `${vehicle.displacement_liters} L`;
    if (vehicle.displacement_cc) return `${vehicle.displacement_cc} cc`;
    return null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('fi-FI');
    } catch {
      return dateStr;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: 'text.primary',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            {isEditing ? (editData.registration_number || vehicle.registration_number) : vehicle.registration_number}
          </Typography>
          <Typography sx={{ fontSize: '15px', color: 'text.secondary' }}>
            {vehicle.manufacturer} {vehicle.model} {vehicle.model_variant}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'text.disabled' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 1 }}>
        {vehicle.id && (
          <InternalNoteEditor
            kind="vehicle"
            note={vehicle.internal_note}
            canEdit={can('vehicles.manage')}
            onSave={handleSaveInternalNote}
            sx={{ mb: 2 }}
          />
        )}
        {vehicle.id && (
          <VehicleServiceInfoEditor
            serviceInfo={vehicle.service_info}
            canEdit={can('vehicles.manage')}
            onSave={handleSaveServiceInfo}
            sx={{ mb: 2 }}
          />
        )}

        {/* Trial Info Box - vehicle lookup is a paid feature */}
        {isTrial() && isVehicleDataIncomplete && vehicle.registration_number && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              mb: 2,
              borderRadius: '10px',
              '& .MuiAlert-message': { width: '100%' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {t('Vehicle lookup is a paid feature')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('vehicleLookup.trialMessage')}
            </Typography>
          </Alert>
        )}

        {/* Edit/Save Buttons */}
        {!isEditing ? (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              sx={{ color: 'text.secondary' }}
            >
              {t('Edit')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ mb: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={handleCancelEdit}
              sx={{ color: 'text.secondary' }}
            >
              {t('Cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={saveLoading ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveVehicle}
              disabled={saveLoading}
            >
              {t('Save')}
            </Button>
          </Box>
        )}

        {/* Edit Form */}
        <Collapse in={isEditing}>
          <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: '10px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t('Registration Number')}
                  value={editData.registration_number || ''}
                  onChange={(e) => {
                    setEditData((prev) => ({
                      ...prev,
                      registration_number: e.target.value.toUpperCase().replace(/\s+/g, ''),
                    }));
                  }}
                  fullWidth
                  size="small"
                  inputProps={{ style: { ...plateFont, fontWeight: 600 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Manufacturer')}
                  value={editData.manufacturer || ''}
                  onChange={handleEditChange('manufacturer')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Model')}
                  value={editData.model || ''}
                  onChange={handleEditChange('model')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Variant')}
                  value={editData.model_variant || ''}
                  onChange={handleEditChange('model_variant')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Year')}
                  value={editData.year_model || ''}
                  onChange={handleEditChange('year_model')}
                  fullWidth
                  size="small"
                  type="number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('VIN')}
                  value={editData.vin || ''}
                  onChange={handleEditChange('vin')}
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 17, style: { ...numFont } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Fuel')}
                  value={editData.fuel || ''}
                  onChange={handleEditChange('fuel')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Engine code')}
                  value={editData.engine_code || ''}
                  onChange={handleEditChange('engine_code')}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Displacement') + ' (cc)'}
                  value={editData.displacement_cc || ''}
                  onChange={handleEditChange('displacement_cc')}
                  fullWidth
                  size="small"
                  type="number"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('Power') + ' (kW)'}
                  value={editData.power_kw || ''}
                  onChange={handleEditChange('power_kw')}
                  fullWidth
                  size="small"
                  type="number"
                />
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Error Alert */}
        {lookupError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setLookupError(null)}>
            {lookupError}
          </Alert>
        )}

        {/* Success Alert */}
        {lookupSuccess && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }} onClose={() => setLookupSuccess(false)}>
            {t('Vehicle info updated successfully')}
          </Alert>
        )}

        {/* Basic Info */}
        <InfoSection
          icon={<DirectionsCarIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('vehicle.basicInfo')}
          items={[
            { label: t('Manufacturer'), value: vehicle.manufacturer },
            { label: t('Model'), value: vehicle.model },
            { label: t('Variant'), value: vehicle.model_variant },
            { label: t('Year'), value: vehicle.year_model },
            { label: t('vehicle.bodyType'), value: vehicle.body_type },
            { label: t('vehicle.category'), value: vehicle.vehicle_category_eu
              ? t(VEHICLE_CATEGORY_MAP[vehicle.vehicle_category_eu] || vehicle.vehicle_category_eu)
              : null },
            { label: t('VIN'), value: vehicle.vin },
          ]}
        />

        {/* Engine */}
        <InfoSection
          icon={<SettingsIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('vehicle.engine')}
          items={[
            { label: t('Engine code'), value: vehicle.engine_code },
            { label: t('Displacement'), value: formatDisplacement() },
            { label: t('Cylinders'), value: vehicle.cylinders },
            { label: t('Valves'), value: vehicle.valves },
            { label: t('Power'), value: formatPower() },
            { label: t('vehicle.engineType'), value: vehicle.engine_type },
          ]}
        />

        {/* Fuel & Drivetrain */}
        <InfoSection
          icon={<LocalGasStationIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('Fuel & Drivetrain')}
          items={[
            { label: t('Fuel'), value: vehicle.fuel },
            { label: t('vehicle.fuelType'), value: vehicle.fuel_type },
            { label: t('vehicle.fuelSystem'), value: vehicle.fuel_system },
            { label: t('vehicle.driveType'), value: vehicle.drive_type },
            { label: t('vehicle.wheelDrive'), value: vehicle.wheel_drive },
            { label: t('vehicle.compressor'), value: vehicle.compressor === true ? t('Yes') : vehicle.compressor === false ? t('No') : null },
          ]}
        />

        {/* Dimensions & Weight */}
        <InfoSection
          icon={<StraightenIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('vehicle.dimensions')}
          items={[
            { label: t('vehicle.curbWeight'), value: vehicle.kerb_weight ? `${vehicle.kerb_weight} kg` : null },
            { label: t('vehicle.grossWeight'), value: vehicle.total_road_weight ? `${vehicle.total_road_weight} kg` : null },
            { label: t('vehicle.length'), value: vehicle.length_mm ? `${vehicle.length_mm} mm` : null },
            { label: t('vehicle.doors'), value: vehicle.doors },
            { label: t('vehicle.axles'), value: vehicle.axles },
          ]}
        />

        {/* Tyres */}
        <InfoSection
          icon={<TireRepairIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('Tyres')}
          items={[
            { label: t('vehicle.frontTyres'), value: vehicle.front_tyres },
            { label: t('vehicle.rearTyres'), value: vehicle.rear_tyres },
          ]}
        />

        {/* Identifiers */}
        <InfoSection
          icon={<FingerprintIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('vehicle.identifiers')}
          items={[
            { label: t('vehicle.typeApproval'), value: vehicle.type_code },
          ]}
        />

        {/* Dates */}
        <InfoSection
          icon={<CalendarTodayIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('Dates')}
          items={[
            { label: t('Registration date'), value: formatDate(vehicle.registration_date) },
            { label: t('Production start'), value: formatDate(vehicle.production_start) },
            { label: t('Model years'), value: vehicle.model_year_from && vehicle.model_year_to
              ? `${vehicle.model_year_from} - ${vehicle.model_year_to}`
              : vehicle.model_year_from || vehicle.model_year_to },
          ]}
        />

        {/* Odometer */}
        <InfoSection
          icon={<SpeedIcon sx={{ fontSize: 18, color: 'secondary.main' }} />}
          title={t('odometer.odometer')}
          items={[
            { label: t('odometer.latestReading'), value: vehicle.latest_odometer
              ? `${vehicle.latest_odometer.toLocaleString('fi-FI')} km`
              : t('odometer.noReading') },
            { label: t('odometer.recordedAt'), value: vehicle.latest_odometer_date
              ? formatDate(vehicle.latest_odometer_date)
              : null },
            { label: t('odometer.hourMeter'), value: vehicle.latest_hour_meter
              ? `${vehicle.latest_hour_meter.toLocaleString('fi-FI')} h`
              : null },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
}

export default withTranslation()(VehicleInfoModal);
